import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PromiseState } from 'react-refetch';

import { EntityContext } from 'context/entityContext';
import connect from 'helpers/apiConnect';
import formatDate, { PeriodKey } from 'helpers/formatDate';
import getColorScale from '../helpers/getColorScale';
import existsIn from '../helpers/existsIn';
import getColumnNumber from '../helpers/getColumnNumber';
import getFirstEntityDate from '../helpers/getFirstEntityDate';
import getTrendPoints from '../helpers/getTrendPoints';
import getDataTogglePairs from '../helpers/getDataTogglePairs';
import getMargins from 'components/data-visualization/LineChart/helpers/getMargins';
import salesTypes from 'maps/sales-types';
import periodTypes from 'maps/period-types';

import useExportURL from 'hooks/useExportURL';
import useMedia from 'hooks/useMedia';
import useTrendData from '../hooks/useTrendData';
import useComponentSettings from 'hooks/useComponentSettings';
import useHasNoData from 'components/data-containers/PerformanceTrend/hooks/useHasNoData';
import {
  getSchema,
  DiscoveryTrends,
} from 'schemas/componentSettings/discoveryPerformanceTrend';
import getDiscoveriesTrendUrl from 'url-constructors/Shazam/getDiscoveriesTrendUrl';

import colors from 'styles/themes/colors';

import GenericComponent from 'components/utility/GenericComponent/GenericComponent';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import ComponentControls from 'components/styled/ComponentControls/ComponentControls';
import KeyValueLoader from 'components/utility/ContentLoader/KeyValueLoader/KeyValueLoader';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import DataToggleList from 'components/DataToggleList/DataToggleList';
import Legend from 'components/data-containers/PerformanceTrend/shared/Legend';
import {
  VisiblePeriod,
  VisiblePeriodWrapper,
  StyledPerformanceTrendLoader,
  Relative,
  DataToggleContainer,
} from '../shared/PerformanceTrend.style';
import LineChartSkeleton from 'components/data-visualization/LineChart/LineChartSkeleton/LineChartSkeleton';
import LineChart from 'components/data-visualization/LineChart/LineChart';
import Chart from 'components/data-visualization/Chart/Chart';
import PeriodDropdown from 'components/utility/PeriodDropdown';
import { ILabelValuePair, TAsyncDispatchResult } from 'types/shared';
import { IDataTogglePairs } from '../types';
import { ITrendDataWithCountry } from '../hooks/useTrendData';

const dataSource: Function = () => {
  return {
    fetchTrendData: (options: any): any => ({
      trendData: getDiscoveriesTrendUrl(options),
    }),
  };
};

interface IDiscoveryTrendProps {
  fetchTrendData: Function;
  trendData: PromiseState<ITrendDataWithCountry[]>;
  glossarySlug: string;
  title: string;
}

const DiscoveriesTrend: React.FC<IDiscoveryTrendProps> = (props) => {
  const {
    fetchTrendData,
    trendData = PromiseState.create(),
    glossarySlug = 'charts-graphs-tables/performance-trend',
    title = 'Shazam Performance',
  } = props;

  const margin: any = getMargins(true);
  const isSmallScreen = useMedia('(max-width: 48em)');
  const salesType = salesTypes.discoveries;
  const {
    entityIds,
    entityType,
    countryIds,
    dataColors,
    filters,
    compareMode,
    customRegionFilter,
  } = useContext(EntityContext);

  const [period, setPeriod] = useState<Date | null>(null);
  const [tempPeriod, setTempPeriod] = useState<Date | null>(null);
  const [selectedTrendPoints, setSelectedTrendPoints] = useState<any[]>();
  const [tempSelectedTrendPoints, setTempSelectedTrendPoints] = useState<
    any[] | null
  >();

  const { fulfilled, pending, rejected, reason } = trendData;
  const exportUrl = useExportURL(trendData);

  /**
   * Component State
   */
  const performanceTrendSchema = getSchema();

  const [{ selectedInterval }, saveComponentSettings] =
    useComponentSettings<DiscoveryTrends>(
      'discoveries-performance-trend',
      performanceTrendSchema
    );

  const interval = periodTypes[selectedInterval];

  /**
   * Get the trend data
   */
  const trends = useTrendData({
    trendData: trendData.value,
    withOutliers: true,
  });

  /**
   * Fetch new data when context, props or state change
   */
  useEffect(() => {
    fetchTrendData({
      entityType,
      entityIds,
      countryIds:
        compareMode === 'country'
          ? countryIds
          : filters
          ? filters.country
          : filters,
      period: selectedInterval,
      region: filters?.region,
      compareMode,
      customRegion: customRegionFilter,
    });
  }, [
    compareMode,
    countryIds,
    entityType,
    entityIds,
    filters,
    fetchTrendData,
    selectedInterval,
    customRegionFilter,
  ]);

  /**
   * Set the initial period
   */
  useEffect(() => {
    if (!trends) return;
    const initialPeriod = getFirstEntityDate(trends);
    setPeriod(initialPeriod);
  }, [trends]);

  const colorScale = useCallback(getColorScale(trends, dataColors), [
    trends,
    dataColors,
  ]);

  /**
   * Create the label/value pairs for DataToggleList
   */
  const dataTogglePairs: IDataTogglePairs[] | undefined = useMemo(
    () =>
      getDataTogglePairs({
        trends,
        period: tempPeriod || period,
        salesType,
        customers: null,
        colorScale,
        customerFilter: null,
      }),
    [colorScale, period, salesType, tempPeriod, trends]
  );

  /**
   * The number of columns to display in the DataToggleList
   * @type {Number}
   */
  const numColumns = getColumnNumber(compareMode, countryIds as any, entityIds);

  /**
   * Boolean to indicate no data.
   */
  const hasNoData = useHasNoData(fulfilled, trends);

  /**
   * Set the currently selected (by clicking) data points
   * @type {Array}
   */
  useEffect(() => {
    if (!period) return;
    setSelectedTrendPoints(getTrendPoints(trends, period));
  }, [trends, period]);

  /**
   * Set the currently temporarily selected (by hovering) data points
   * @type {Array}
   */
  useEffect(() => {
    if (tempPeriod) {
      setTempSelectedTrendPoints(getTrendPoints(trends, tempPeriod));
    } else {
      setTempSelectedTrendPoints(null);
    }
  }, [trends, tempPeriod]);

  /**
   * Update the time period displayed when you move the mouse out of the trend graph
   * Set this time period by clicking or tapping in the graph.
   *
   * @param {Date} nextPeriod timestamp in ms
   */
  const onPeriodChange: Function = (nextPeriod: Date) => {
    if (existsIn(trends, nextPeriod)) {
      setPeriod(nextPeriod);
    }
  };

  /**
   * Update the time period displayed when you hover in the graph. Only displayed
   * for devices without touch capabilities.
   *
   * @param  {Date} nextPeriod timestamp in ms
   */
  const onTempPeriodChange: Function = (nextPeriod: Date) => {
    if (existsIn(trends, nextPeriod)) {
      setTempPeriod(nextPeriod);
    }

    // When a user moves the mouse out of the linechart hitbox, this is called with null.
    if (nextPeriod === null) {
      setTempPeriod(nextPeriod);
    }
  };

  return (
    <GenericComponent
      title={title}
      glossarySlug={glossarySlug}
      allowedFilters={['country', 'region', 'customRegion']}
      loading={pending}
      success={fulfilled}
      error={rejected}
      extraToolbarItems={[
        {
          href: exportUrl,
          label: 'Export data',
        },
      ]}
      isNarrowScreen
    >
      <ErrorBoundary
        error={reason}
        errorInfo={reason ? reason.cause : undefined}
      >
        <ComponentControls>
          <VisiblePeriodWrapper>
            {period && (
              <VisiblePeriod
                label={selectedInterval}
                value={formatDate(
                  new Date(tempPeriod || period),
                  selectedInterval
                )}
                dataColor={colors.DeepBlue}
              />
            )}
            {!period && <KeyValueLoader />}
          </VisiblePeriodWrapper>

          <PeriodDropdown
            selectedItem={interval}
            onChange={(
              interval: ILabelValuePair<string>
            ): TAsyncDispatchResult =>
              saveComponentSettings({
                selectedInterval: interval.value as PeriodKey,
              })
            }
            isRoundedDropdown="all"
          />
        </ComponentControls>
        <AnimatedLoadingWrapper loading={pending}>
          <DataToggleContainer>
            <DataToggleList
              data={dataTogglePairs}
              loading={pending}
              expectedItemCount={numColumns}
            />
          </DataToggleContainer>
          <Relative>
            <Chart
              height={isSmallScreen ? 260 : 420}
              hasNoData={(fulfilled && !trends) || hasNoData}
              mediaQuery="(min-width: 40em)"
              isDimmed={pending}
              render={(renderProps: any): JSX.Element => (
                <>
                  {selectedTrendPoints && (
                    <LineChart
                      showVerticalAxisLabels
                      fillFromLastNonNullValue
                      shouldTransitionIn={fulfilled}
                      period={interval.value}
                      colorScale={colorScale}
                      entities={trends}
                      selectedEntities={selectedTrendPoints}
                      temporarySelectedEntities={tempSelectedTrendPoints}
                      handleMouseHover={onTempPeriodChange}
                      handleMouseClick={onPeriodChange}
                      hasNoData={trends.length === 0 || hasNoData}
                      label={salesType.label}
                      {...renderProps}
                      isStrictDesign
                      isLabelRotated
                      trackerLineStart
                      isCutGrid={true}
                    />
                  )}
                  {!selectedTrendPoints && (
                    <LineChartSkeleton
                      width={renderProps.parentWidth}
                      height={
                        renderProps.parentHeight - margin.top - margin.bottom
                      }
                      marginTop={margin.top}
                    />
                  )}
                </>
              )}
            />
            {pending && !selectedTrendPoints && (
              <StyledPerformanceTrendLoader />
            )}
          </Relative>
        </AnimatedLoadingWrapper>
        <Legend />
      </ErrorBoundary>
    </GenericComponent>
  );
};

export default connect(dataSource as any)(DiscoveriesTrend as any);
